/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { useRef } from 'react';
import {
    Dialog,
    DIALOG_SIZE,
    TOOLBAR_ITEM_TYPE,
    TOOLBAR_VIEW_TYPE,
    Search,
    ACTION_VIEW,
    ConfiguredGrid,
    SortingMode,
    ConfiguredToolbar,
    useGlobalLang,
} from '@veeam-vspc/shared/components';
import { Vb365JobItemComposedItemType } from '@veeam-vspc/models/rest';

import type { Vb365JobItemUser, Vb365JobItemComposed } from '@veeam-vspc/models/rest';
import type { GridStore } from '@veeam-vspc/shared/components';
import type { FC } from 'react';
import type { VspcLang } from 'configs/languages';

import { useJobWizardStore } from '../../../../stores';
import { backupModeService } from '../../services';
import { ObjectTypeCell } from '../ObjectTypeCell/ObjectTypeCell';

import type { UsersModalProps } from '../../interfaces';

export const UsersModal: FC<UsersModalProps> = ({ onCancel, onApply }) => {
    const lang = useGlobalLang<VspcLang>();
    const wizardStore = useJobWizardStore();
    const api = useRef<GridStore<Vb365JobItemUser, unknown, unknown>>();

    const apply = () => {
        const selected = api.current.selected;

        onApply(selected);
    };

    const { vb365ServerUid, instanceUid } = wizardStore.selectedOrganization;

    return (
        <Dialog
            header={lang.ADD_USERS}
            size={{
                width: DIALOG_SIZE.xl,
                height: DIALOG_SIZE.xl,
            }}
            onRequestClose={onCancel}
            actions={[
                { text: lang.ADD, onClick: apply },
                { text: lang.CLOSE, onClick: onCancel, view: ACTION_VIEW.secondary },
            ]}
        >
            <ConfiguredGrid
                api={api}
                columns={[
                    { id: 'displayName', title: lang.NAME, cell: rowData => rowData.displayName },
                    { id: 'name', title: lang.EMAIL, cell: rowData => rowData.name },
                    { id: 'officeName', title: lang.OFFICE, cell: rowData => rowData.officeName },
                    {
                        id: 'userType',
                        title: lang.TYPE,
                        cell: rowData => (
                            <ObjectTypeCell
                                value={
                                    {
                                        user: { userType: rowData.userType },
                                        itemType: Vb365JobItemComposedItemType.User,
                                    } as Vb365JobItemComposed
                                }
                            />
                        ),
                    },
                ]}
                data={backupModeService.getUsers(vb365ServerUid, instanceUid, api.current)}
                defaultColumnMinWidth={100}
                disableAutoUpdate
                sortingMode={SortingMode.Local}
                useCompactPaginationWithCurrentPage
                toolbars={[
                    () => (
                        <ConfiguredToolbar
                            view={TOOLBAR_VIEW_TYPE.spotted}
                            items={[
                                {
                                    type: TOOLBAR_ITEM_TYPE.customControl,
                                    render: () => (
                                        <Search
                                            name='userDisplayNameFilter'
                                            placeholder={lang.USER}
                                        />
                                    ),
                                },
                            ]}
                        />
                    ),
                ]}
                selection={{
                    field: 'id',
                    checkbox: true,
                    multiple: true,
                }}
                isRowDisabled={rowData => wizardStore.isItemSelected(rowData.id, Vb365JobItemComposedItemType.User)}
            />
        </Dialog>
    );
};
